import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import ActivityTimeline from "../components/ActivityTimeline";
import AttachmentList from "../components/AttachmentList";
import AttachmentUpload from "../components/AttachmentUpload";
import CommentThread from "../components/CommentThread";
import FeedbackWidget from "../components/FeedbackWidget";
import ClosureHistory from "../components/ClosureHistory";
import ResolutionDialog from "../components/ResolutionDialog";
import AssignDialog from "../components/AssignDialog";
import { Sparkles } from "lucide-react";

// v1.9: resolved/closed move through the modal (resolution notes required + captured in history)
const STATUSES = ["identified", "under_investigation", "known_error"];
const CLOSURE_DIALOG_CONFIG = {
  resolved: { title: "Resolve problem", actionLabel: "Resolve",        fieldLabel: "Resolution",       tone: "primary", required: true },
  closed:   { title: "Close problem",   actionLabel: "Close & Archive", fieldLabel: "Closure notes",   tone: "primary", required: true },
};

export default function ProblemDetail() {
  const { id } = useParams();
  const { user, hasRole } = useAuth();
  const [p, setP] = useState(null);
  const [users, setUsers] = useState([]);
  const [rc, setRc] = useState(""); const [wa, setWa] = useState("");
  const [newAttachments, setNewAttachments] = useState([]);
  const [dialogAction, setDialogAction] = useState(null);
  // v2.2 #1: in-app Assign modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSuggestion, setAssignSuggestion] = useState(null);
  const isIT = hasRole("admin", "it_team", "it_manager");

  async function load() {
    const { data } = await api.get(`/api/problems/${id}`); setP(data);
    setRc(data.root_cause || ""); setWa(data.workaround || "");
  }
  useEffect(() => {
    load(); if (isIT) api.get("/api/users").then((r) => setUsers(r.data));
  }, [id]); // eslint-disable-line

  async function update(payload) {
    try { const { data } = await api.put(`/api/problems/${id}`, payload); setP(data); return data; }
    catch (e) { alert(e.response?.data?.detail || "Update failed"); throw e; }
  }

  async function openAssignDialog() {
    try {
      const { data } = await api.get(`/api/users/suggest-assignee?ticket_type=problem&category=${encodeURIComponent(p.category)}`);
      if (data.assignee) {
        setAssignSuggestion({ ...data.assignee, open_count: data.open_count, category_familiarity: data.category_familiarity });
      } else {
        setAssignSuggestion(null);
      }
    } catch {
      setAssignSuggestion(null);
    }
    setAssignOpen(true);
  }
  async function handleAssignSubmit(userId) {
    await update({ assigned_to_id: userId });
    setAssignOpen(false);
  }

  async function handleDialogSubmit(text) {
    if (!dialogAction) return;
    await update({ status: dialogAction, resolution: text });
    setDialogAction(null);
  }

  if (!p) return <div>Loading…</div>;

  const dlg = dialogAction ? CLOSURE_DIALOG_CONFIG[dialogAction] : null;
  // v2.0 #8
  const isClosedP = ["resolved", "closed"].includes(p.status);

  return (
    <div className="space-y-4">
      <Link to="/problems" className="text-sm text-brand-600 hover:underline flex items-center gap-1"><ArrowLeft size={14} /> Back</Link>
      <div className="card p-5">
        <div className="flex justify-between">
          <div>
            <div className="text-xs font-mono text-gray-500">{p.problem_number}</div>
            <h1 className="text-xl font-bold">{p.title}</h1>
            <div className="text-sm text-gray-600">Reporter: {p.reporter_name} · Plant: {p.plant} · Priority: {p.priority}</div>
          </div>
          <span className="badge bg-brand-50 text-brand-700">{p.status}</span>
        </div>
        <p className="mt-4 text-sm whitespace-pre-wrap">{p.description}</p>
      </div>

      {isIT && (
        <div className="card p-5 space-y-3">
          <h2 className="font-semibold">Investigation</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label flex items-center justify-between">
                <span>Assignee</span>
                {!isClosedP && (
                  <button type="button" onClick={openAssignDialog} className="text-xs text-brand-600 hover:underline flex items-center gap-1">
                    <Sparkles size={12} /> Suggest by workload
                  </button>
                )}
              </label>
              {isClosedP ? (
                <div className="input bg-slate-50 text-slate-600 cursor-not-allowed">
                  {p.assigned_to_name || "— Unassigned —"}
                  <span className="ml-2 text-xs text-slate-500">(reopen to reassign)</span>
                </div>
              ) : (
                <select className="input" value={p.assigned_to_id || ""} onChange={(e) => update({ assigned_to_id: e.target.value || null })}>
                  <option value="">— Unassigned —</option>
                  {users.filter((u) => ["it_team", "it_manager", "it_security"].includes(u.role)).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="label">Status (raw)</label>
              <select className="input" value={STATUSES.includes(p.status) ? p.status : ""}
                onChange={(e) => { if (e.target.value) update({ status: e.target.value }); }}>
                {!STATUSES.includes(p.status) && <option value="">{p.status} (closure)</option>}
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Resolve / Close captures permanent notes via the action buttons below.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="label">Root cause</label>
              <textarea className="input" rows="2" value={rc} onChange={(e) => setRc(e.target.value)} />
              <button className="btn-secondary mt-1" onClick={() => update({ root_cause: rc })}>Save</button>
            </div>
            <div className="sm:col-span-2">
              <label className="label">Workaround</label>
              <textarea className="input" rows="2" value={wa} onChange={(e) => setWa(e.target.value)} />
              <button className="btn-secondary mt-1" onClick={() => update({ workaround: wa })}>Save</button>
            </div>
            <div className="sm:col-span-2 flex flex-wrap gap-2">
              {!["resolved", "closed"].includes(p.status) && (
                <>
                  <button className="btn-primary" onClick={() => setDialogAction("resolved")}>Resolve…</button>
                  <button className="btn-primary" onClick={() => setDialogAction("closed")}>Close…</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isIT && (
        <div className="card p-5 space-y-2">
          <h2 className="font-semibold">Add attachments</h2>
          <AttachmentUpload value={newAttachments} onChange={setNewAttachments} />
          {newAttachments.length > 0 && (
            <button className="btn-primary" onClick={async () => { await update({ attachments: newAttachments }); setNewAttachments([]); }}>
              Upload {newAttachments.length} file(s)
            </button>
          )}
        </div>
      )}

      <AttachmentList items={p.attachments} />

      <ClosureHistory entries={p.closure_history || []} title="Resolution history" />

      <CommentThread kind="problems" ticket={p} onPosted={load} />

      <FeedbackWidget kind="problems" ticket={p} currentUser={user} onSubmitted={() => load()} />

      <div className="card p-5">
        <h2 className="font-semibold mb-3">Activity Timeline</h2>
        <ActivityTimeline entries={p.activity_log} />
      </div>

      {dlg && (
        <ResolutionDialog
          open={!!dialogAction}
          onClose={() => setDialogAction(null)}
          onSubmit={handleDialogSubmit}
          title={dlg.title}
          fieldLabel={dlg.fieldLabel}
          actionLabel={dlg.actionLabel}
          tone={dlg.tone}
          required={dlg.required}
        />
      )}

      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSubmit={handleAssignSubmit}
        suggested={assignSuggestion}
        users={users}
        allowedRoles={["it_team", "it_manager", "it_security"]}
        ticketCategory={p.category}
        ticketNumber={p.problem_number}
      />
    </div>
  );
}
